import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plane, Plus, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import {
  type TripStatus,
  type TripSummary,
  createTrip,
  formatAmount,
  listTrips,
} from '@/lib/tickets-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface UserLite {
  id: string;
  fullName: string;
  email: string;
  isActive: boolean;
}

/**
 * "Tickets equipo" para RRHH. Lista los viajes y permite crear uno nuevo
 * asignando empleados (se genera una carpeta por cada uno). Reservado a
 * HR_ADMIN / SUPER_ADMIN (el controller hace cumplir el rol).
 */
export function AdminTrips() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<TripStatus | 'ALL'>('OPEN');
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ['admin', 'trips', filter],
    queryFn: () => listTrips(filter === 'ALL' ? undefined : filter),
  });

  const trips = list.data ?? [];

  return (
    <div className="p-6 md:p-8">
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold tracking-tightish">Tickets de viaje</h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              Crea un viaje y asigna a los empleados que van. A cada uno le
              aparecerá su carpeta para subir tickets. Al terminar, cierra el
              viaje y descarga el PDF / Excel de cada empleado.
            </p>
          </div>
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-3.5 w-3.5" />
            Nuevo viaje
          </Button>
        </header>

        <div className="px-7 py-3 border-b border-border flex items-center gap-1">
          <FilterBtn label="En curso" active={filter === 'OPEN'} onClick={() => setFilter('OPEN')} />
          <FilterBtn
            label="Cerrados"
            active={filter === 'CLOSED'}
            onClick={() => setFilter('CLOSED')}
          />
          <FilterBtn label="Todos" active={filter === 'ALL'} onClick={() => setFilter('ALL')} />
        </div>

        {list.isLoading ? (
          <p className="px-7 py-8 text-sm text-muted-foreground">Cargando…</p>
        ) : list.error ? (
          <p role="alert" className="px-7 py-8 text-sm text-destructive">
            {list.error instanceof ApiError ? list.error.message : 'Error cargando viajes'}
          </p>
        ) : trips.length === 0 ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">
            No hay viajes en esta vista.
          </p>
        ) : (
          <table className="w-full text-[14px]">
            <thead>
              <tr>
                <Th>Viaje</Th>
                <Th align="center">Empleados</Th>
                <Th align="center">Tickets</Th>
                <Th align="right">Total</Th>
                <Th align="center">Estado</Th>
                <Th>Cerrado</Th>
              </tr>
            </thead>
            <tbody>
              {trips.map((t) => (
                <TripRow key={t.id} trip={t} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating ? (
        <CreateTripModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            void qc.invalidateQueries({ queryKey: ['admin', 'trips'] });
            setCreating(false);
          }}
        />
      ) : null}
    </div>
  );
}

function FilterBtn({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-1 rounded-[3px] text-[12px] font-medium',
        active
          ? 'bg-muted/40 border border-border text-foreground'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

function TripRow({ trip }: { trip: TripSummary }) {
  return (
    <tr className="border-b border-border last:border-b-0 hover:bg-muted/30">
      <td className="px-5 py-3.5">
        <Link
          to={`/admin/viajes/${trip.id}`}
          className="font-medium hover:text-icaria-600 flex items-center gap-2"
        >
          <Plane className="h-3.5 w-3.5 text-muted-foreground" />
          {trip.name}
        </Link>
        {trip.tripStartDate ? (
          <p className="text-[12px] text-muted-foreground mt-0.5 pl-5.5">
            {trip.tripStartDate}
            {trip.tripEndDate && trip.tripEndDate !== trip.tripStartDate
              ? ` → ${trip.tripEndDate}`
              : ''}
          </p>
        ) : null}
      </td>
      <td className="px-5 py-3.5 text-center">{trip.employeeCount}</td>
      <td className="px-5 py-3.5 text-center">{trip.ticketCount}</td>
      <td className="px-5 py-3.5 text-right font-mono">
        {trip.totalCents > 0 ? formatAmount(trip.totalCents) : '—'}
      </td>
      <td className="px-5 py-3.5 text-center">
        <span
          className={cn(
            'font-mono text-[10px] font-medium uppercase border rounded-[3px] px-2 py-0.5',
            trip.status === 'OPEN'
              ? 'bg-success/10 border-success/40 text-success'
              : 'bg-muted border-border text-muted-foreground',
          )}
          style={{ letterSpacing: '0.06em' }}
        >
          {trip.status === 'OPEN' ? 'En curso' : 'Cerrado'}
        </span>
      </td>
      <td className="px-5 py-3.5 text-[12.5px] text-muted-foreground">
        {trip.closedAt ? new Date(trip.closedAt).toLocaleDateString() : '—'}
      </td>
    </tr>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
}) {
  return (
    <th
      className={cn(
        'px-5 py-3 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground border-b border-border',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
        align === 'left' && 'text-left',
      )}
    >
      {children}
    </th>
  );
}

function CreateTripModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api<UserLite[]>('/admin/users'),
  });
  const employees = (usersQuery.data ?? []).filter((u) => u.isActive);

  const create = useMutation({
    mutationFn: () =>
      createTrip({
        name: name.trim(),
        description: description.trim() || undefined,
        tripStartDate: start || undefined,
        tripEndDate: end || undefined,
        employeeUserIds: [...selected],
      }),
    onSuccess: () => onCreated(),
  });

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const canSubmit = name.trim().length > 0 && selected.size > 0 && !create.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg bg-card border border-border rounded-sm shadow-xl max-h-[95dvh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-card">
          <h2 className="text-base font-semibold">Nuevo viaje</h2>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <form
          className="p-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) create.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="trip-name">Nombre del viaje</Label>
            <Input
              id="trip-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Feria Madrid · junio 2026"
              autoFocus
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="trip-start">Inicio</Label>
              <Input
                id="trip-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="trip-end">Fin</Label>
              <Input
                id="trip-end"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="trip-desc">Notas (opcional)</Label>
            <Textarea
              id="trip-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Empleados que van ({selected.size} seleccionados)</Label>
            <div className="border border-border rounded-sm max-h-56 overflow-y-auto divide-y divide-border">
              {usersQuery.isLoading ? (
                <p className="px-3 py-3 text-[13px] text-muted-foreground">Cargando empleados…</p>
              ) : employees.length === 0 ? (
                <p className="px-3 py-3 text-[13px] text-muted-foreground">
                  No hay empleados activos.
                </p>
              ) : (
                employees.map((u) => (
                  <label
                    key={u.id}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/30"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggle(u.id)}
                      className="h-4 w-4 accent-icaria-600"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="text-[14px] font-medium block truncate">{u.fullName}</span>
                      <span className="text-[12px] text-muted-foreground block truncate">
                        {u.email}
                      </span>
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          {create.error ? (
            <p role="alert" className="text-sm text-destructive">
              {create.error instanceof Error ? create.error.message : 'Error creando el viaje'}
            </p>
          ) : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={create.isPending}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={!canSubmit}>
              {create.isPending ? 'Creando…' : 'Crear viaje'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
